import React, { useEffect, useState } from "react";
import { supabase } from "./supabase/auth";
import { Ingredient, PROTEIN_CLASS_LABELS } from "./helpers/saveIngredient";
import { getDataSource } from "./helpers/getDataSource";
import { useTranslation } from 'react-i18next';

const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const sourceStyle: React.CSSProperties = {
  fontSize: "9px",
  color: "#888",
  fontStyle: "normal",
  padding: "0 10px 2px",
  background: "#fafafa",
};

const sectionBox: React.CSSProperties = {
  border: "1px solid #d0d0d0",
  background: "#ffffff",
  marginBottom: "16px",
};

const sectionTitle: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  background: "#f0f0f0",
  borderBottom: "1px solid #d0d0d0",
  color: "#000000",
};

const fieldRow: React.CSSProperties = {
  display: "flex",
  borderBottom: "1px solid #d0d0d0",
  fontSize: "12px",
};

const fieldLabel: React.CSSProperties = {
  width: "180px",
  minWidth: "180px",
  padding: "4px 10px",
  background: "#f8f8f8",
  fontWeight: 600,
  color: "#333333",
  borderRight: "1px solid #d0d0d0",
};

const fieldValue: React.CSSProperties = {
  flex: 1,
  padding: "4px 10px",
  color: "#000000",
  fontFamily: mono,
};

const nullVal: React.CSSProperties = { ...fieldValue, color: "#999999" };

const tabStyle: React.CSSProperties = {
  padding: "6px 16px",
  fontSize: "12px",
  fontWeight: 500,
  border: "1px solid #d0d0d0",
  borderBottom: "none",
  background: "#f0f0f0",
  cursor: "pointer",
  color: "#333333",
  marginRight: "-1px",
};

const activeTab: React.CSSProperties = {
  ...tabStyle,
  background: "#ffffff",
  fontWeight: 700,
  color: "#000000",
};

const btnStyle: React.CSSProperties = {
  padding: "4px 14px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid #d0d0d0",
  background: "#f0f0f0",
  cursor: "pointer",
  color: "#000000",
};

interface ChangeLogEntry {
  id: string;
  changed_at: string;
  changed_by_email: string;
  changed_column: string;
  old_value: string | null;
  new_value: string | null;
  change_reason: string | null;
}

const WHO_REF: Record<string, number> = {
  aa_his: 19, aa_ile: 28, aa_leu: 66, aa_lys: 58,
  aa_mc: 25, aa_pt: 63, aa_thr: 34, aa_trp: 11, aa_val: 35,
};

const AA_LABELS: Record<string, string> = {
  aa_his: "His", aa_ile: "Ile", aa_leu: "Leu", aa_lys: "Lys",
  aa_mc: "M+C", aa_pt: "P+T", aa_thr: "Thr", aa_trp: "Trp", aa_val: "Val",
};

function Field({ label, value, isMono }: { label: string; value: React.ReactNode; isMono?: boolean }) {
  return (
    <div style={fieldRow}>
      <div style={fieldLabel}>{label}</div>
      <div style={value == null || value === "" || value === "-" ? nullVal : (isMono !== false ? fieldValue : { ...fieldValue, fontFamily: "inherit" })}>
        {value == null || value === "" ? "-" : value}
      </div>
    </div>
  );
}

function NumField({ label, value, suffix }: { label: string; value: number | null; suffix?: string }) {
  return <Field label={label} value={value != null ? `${value}${suffix || ""}` : null} />;
}

function SourceRow({ source }: { source: string }) {
  if (!source) return null;
  return (
    <div style={sourceStyle}>
      <span style={{ color: "#aaa" }}>Source:</span> {source}
    </div>
  );
}

type TabId = "overview" | "variants" | "observations" | "history" | "attachments";

export default function IngredientDetailScreen({
  ingredientId,
  onBack,
  onEdit,
}: {
  ingredientId: string;
  onBack: () => void;
  onEdit: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [ing, setIng] = useState<Ingredient | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>("overview");
  const [changeLog, setChangeLog] = useState<ChangeLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [variantCount, setVariantCount] = useState<number>(0);
  const [obsCount, setObsCount] = useState<number>(0);
  const [attachCount, setAttachCount] = useState<number>(0);
  const [showSources, setShowSources] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("ingredients").select("*").eq("id", ingredientId).single();
      setIng(data as Ingredient | null);
      setLoading(false);
    })();
    // Counts for tabs
    supabase.from("ingredient_variants").select("id", { count: "exact", head: true }).eq("ingredient_id", ingredientId)
      .then(({ count }) => setVariantCount(count || 0));
    supabase.from("observations").select("id", { count: "exact", head: true }).eq("ingredient_id", ingredientId)
      .then(({ count }) => setObsCount(count || 0));
    supabase.from("attachments").select("id", { count: "exact", head: true }).eq("ingredient_id", ingredientId)
      .then(({ count }) => setAttachCount(count || 0));
  }, [ingredientId]);

  useEffect(() => {
    if (tab !== "history") return;
    setLogLoading(true);
    supabase
      .from("data_change_log")
      .select("*")
      .eq("ingredient_id", ingredientId)
      .order("changed_at", { ascending: false })
      .then(({ data }) => {
        setChangeLog((data as ChangeLogEntry[]) || []);
        setLogLoading(false);
      });
  }, [tab, ingredientId]);

  if (loading) {
    return <div style={{ color: "#666", padding: "20px", textAlign: "center", fontSize: "12px" }}>{t('ingredientDetail.loading')}</div>;
  }
  if (!ing) {
    return <div style={{ color: "#666", padding: "20px", textAlign: "center", fontSize: "12px" }}>{t('ingredientDetail.notFound')}</div>;
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: "overview", label: t('ingredientDetail.overview') },
    { id: "variants", label: `${t('ingredientDetail.variants')} (${variantCount})` },
    { id: "observations", label: `${t('ingredientDetail.observations')} (${obsCount})` },
    { id: "history", label: t('ingredientDetail.history') },
    { id: "attachments", label: `${t('ingredientDetail.attachments')} (${attachCount})` },
  ];

  const aaKeys = Object.keys(WHO_REF);
  const maxBar = Math.max(...aaKeys.map((k) => Math.max((ing as any)[k] || 0, WHO_REF[k])), 1);
  const barW = 500;
  const barH = aaKeys.length * 28 + 10;

  return (
    <div style={{ background: "#ffffff", padding: "8px", minHeight: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
        <button style={btnStyle} onClick={onBack}>{t('ingredientDetail.back')}</button>
        <span style={{ fontSize: "14px", fontWeight: 700, color: "#000" }}>{ing.common_name}{i18n.language === 'ko' && ing.korean_name && <span style={{ fontWeight: 400, color: "#666" }}> ({ing.korean_name})</span>}</span>
        <span style={{ fontSize: "12px", color: "#666", fontFamily: mono }}>{ing.id}</span>
        <button
          style={{ ...btnStyle, marginLeft: "auto", ...(showSources ? { background: "#e8e8e8", fontWeight: 700 } : {}) }}
          onClick={() => setShowSources(!showSources)}
        >
          {showSources ? t('ingredientDetail.hideSources') : t('ingredientDetail.showSources')}
        </button>
        <button style={btnStyle} onClick={() => onEdit(ing.id)}>{t('ingredientDetail.edit')}</button>
      </div>

      {/* Primary Data Source banner */}
      {showSources && ing.data_sources?.["_default"] && (
        <div style={{
          padding: "6px 10px",
          marginBottom: "12px",
          background: "#f0f0ff",
          border: "1px solid #c0c0e0",
          fontSize: "11px",
          color: "#333",
        }}>
          <strong style={{ color: "#555" }}>{t('ingredientDetail.primaryDataSource')}</strong> {ing.data_sources["_default"]}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #d0d0d0", marginBottom: "16px" }}>
        {tabs.map((t) => (
          <button key={t.id} style={tab === t.id ? activeTab : tabStyle} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview" && (
        <div style={{ maxWidth: "800px" }}>
          {/* Identity */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientForm.identity')}</div>
            <Field label={t('ingredientForm.id')} value={ing.id} />
            <Field label={t('ingredientForm.commonName')} value={ing.common_name} />
            <Field label={t('ingredientForm.koreanName')} value={ing.korean_name} />
            <Field label={t('ingredientForm.fdaName')} value={ing.fda_name} />
            <Field label={t('ingredientForm.supplier')} value={ing.supplier} />
            <Field label={t('ingredientForm.color')} value={
              ing.color ? (
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ display: "inline-block", width: "16px", height: "16px", background: ing.color, border: "1px solid #ccc" }} />
                  {ing.color}
                </span>
              ) : null
            } />
            <Field label={t('ingredientForm.tags')} value={
              ing.tags && ing.tags.length > 0
                ? <span>{ing.tags.map((t, i) => (
                    <span key={i} style={{ display: "inline-block", padding: "1px 6px", background: "#e8e8e8", border: "1px solid #ccc", marginRight: "4px", fontSize: "11px" }}>{t}</span>
                  ))}</span>
                : null
            } />
            <Field
              label={t('ingredientForm.proteinClass', { defaultValue: i18n.language === 'ko' ? "단백질 분류" : "Protein class" })}
              value={ing.protein_class && PROTEIN_CLASS_LABELS[ing.protein_class]
                ? t(PROTEIN_CLASS_LABELS[ing.protein_class].key, { defaultValue: PROTEIN_CLASS_LABELS[ing.protein_class][i18n.language === 'ko' ? "ko" : "en"] })
                : (ing.protein_class || null)}
              isMono={false}
            />
            <Field label={t('ingredientDetail.active')} value={ing.is_active ? t('ingredientDetail.yes') : t('ingredientDetail.no')} />
          </div>

          {/* Nutrition */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientForm.nutrition')}</div>
            {([
              [t('ingredientForm.proteinG100g'), "protein_per_100g", ing.protein_per_100g],
              [t('ingredientForm.fatLabel'), "fat", ing.fat],
              [t('ingredientForm.saturatedFat'), "satfat", ing.satfat],
              [t('ingredientForm.transFat'), "transfat", ing.transfat],
              [t('ingredientForm.cholesterol'), "cholesterol", ing.cholesterol],
              [t('ingredientForm.carbsLabel'), "carbs", ing.carbs],
              [t('ingredientForm.fiberLabel'), "fiber", ing.fiber],
              [t('ingredientForm.sugar'), "sugar", ing.sugar],
              [t('ingredientForm.sodium'), "sodium", ing.sodium],
              [t('ingredientForm.moistureLabel'), "moisture", ing.moisture],
              [t('ingredientForm.ashLabel'), "ash", (ing as any).ash],
            ] as [string, string, number | null][]).map(([label, key, val]) => (
              <React.Fragment key={key}>
                <NumField label={label} value={val} />
                {showSources && <SourceRow source={getDataSource(ing.data_sources, key)} />}
              </React.Fragment>
            ))}
          </div>

          {/* Amino Acids */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientDetail.aminoAcids')}</div>
            {showSources && (
              <SourceRow source={getDataSource(ing.data_sources, "aa_profile")} />
            )}
            {aaKeys.map((k) => (
              <NumField key={k} label={`${AA_LABELS[k]} (mg/g)`} value={(ing as any)[k]} />
            ))}
            {/* BCAA summary */}
            <div style={{ ...fieldRow, background: "#f8f8ff" }}>
              <div style={{ ...fieldLabel, fontStyle: "italic", color: "#555" }}>{t('ingredientForm.bcaa')}</div>
              <div style={fieldValue}>
                {ing.bcaa_mg_per_g != null ? `${ing.bcaa_mg_per_g} mg/g` : "-"}
                <span style={{ fontSize: "9px", color: "#999", marginLeft: "8px" }}>{t('ingredientForm.calculated')}</span>
              </div>
            </div>
            {showSources && <SourceRow source={getDataSource(ing.data_sources, "bcaa_mg_per_g")} />}
            {/* Bar chart */}
            <div style={{ padding: "10px", borderTop: "1px solid #d0d0d0" }}>
              <div style={{ fontSize: "11px", fontWeight: 600, marginBottom: "6px", color: "#333" }}>
                {t('ingredientDetail.aminoAcidProfileVsWHO')}
              </div>
              <div style={{ border: "1px solid #d0d0d0", background: "#ffffff", padding: "8px", display: "inline-block" }}>
                <svg width={barW} height={barH} style={{ display: "block" }}>
                  {aaKeys.map((k, i) => {
                    const val = (ing as any)[k] || 0;
                    const ref = WHO_REF[k];
                    const y = i * 28 + 5;
                    const scale = (barW - 60) / maxBar;
                    return (
                      <g key={k}>
                        <text x={0} y={y + 14} fontSize="11" fill="#333" fontFamily={mono}>{AA_LABELS[k]}</text>
                        <rect x={40} y={y} width={val * scale} height={12} fill="#4a90d9" opacity={0.7} />
                        <line x1={40 + ref * scale} y1={y - 2} x2={40 + ref * scale} y2={y + 16} stroke="#cc3333" strokeWidth={2} />
                        <text x={40 + val * scale + 4} y={y + 11} fontSize="10" fill="#333" fontFamily={mono}>{val}</text>
                      </g>
                    );
                  })}
                  <text x={barW - 80} y={barH - 2} fontSize="9" fill="#cc3333">-- WHO ref</text>
                </svg>
              </div>
            </div>
          </div>

          {/* Micronutrients */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientForm.micronutrients')}</div>
            {([
              [t('ingredientForm.vitaminD'), "vitamin_d", ing.vitamin_d],
              [t('ingredientForm.calcium'), "calcium", ing.calcium],
              [t('ingredientForm.iron'), "iron", ing.iron],
              [t('ingredientForm.potassium'), "potassium", ing.potassium],
            ] as [string, string, number | null][]).map(([label, key, val]) => (
              <React.Fragment key={key}>
                <NumField label={label} value={val} />
                {showSources && <SourceRow source={getDataSource(ing.data_sources, key)} />}
              </React.Fragment>
            ))}
          </div>

          {/* Fatty Acid Profile */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientForm.fattyAcidProfile')}</div>
            {showSources && <SourceRow source={getDataSource(ing.data_sources, "fa_ala")} />}
            {([
              [t('ingredientForm.ala'), "fa_ala", ing.fa_ala],
              [t('ingredientForm.la'), "fa_la", ing.fa_la],
              [t('ingredientForm.oleic'), "fa_oleic", ing.fa_oleic],
              [t('ingredientForm.palmitic'), "fa_palmitic", ing.fa_palmitic],
              [t('ingredientForm.stearic'), "fa_stearic", ing.fa_stearic],
            ] as [string, string, number | null][]).map(([label, key, val]) => (
              <NumField key={key} label={label} value={val} suffix=" g/100g" />
            ))}
          </div>

          {/* Fiber Fractions */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientForm.fiberFractions')}</div>
            {showSources && <SourceRow source={getDataSource(ing.data_sources, "fiber_insoluble")} />}
            <NumField label={t('ingredientForm.totalDietaryFiber')} value={ing.fiber} suffix=" g/100g" />
            <NumField label={t('ingredientForm.insolubleFiber')} value={ing.fiber_insoluble} suffix=" g/100g" />
            <NumField label={t('ingredientForm.solubleFiber')} value={ing.fiber_soluble} suffix=" g/100g" />
            <NumField label={t('ingredientForm.resistantStarch')} value={ing.resistant_starch} suffix=" g/100g" />
            {showSources && ing.data_sources?.["resistant_starch"] && (
              <SourceRow source={getDataSource(ing.data_sources, "resistant_starch")} />
            )}
          </div>

          {/* Protein Digestibility — meaningless for zero-protein ingredients, so hidden there */}
          {(ing.protein_per_100g || 0) > 0 && (
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientForm.proteinDigestibility')}</div>
            <NumField label={t('ingredientForm.ingredientDigestibility')} value={ing.digestibility} />
            {showSources && <SourceRow source={getDataSource(ing.data_sources, "digestibility")} />}
            <Field label={t('ingredientForm.referenceProduct')} value={ing.ref_digest_product} isMono={false} />
            <NumField label={t('ingredientForm.referenceDigestibility')} value={ing.ref_digest_value} />
            <Field label={t('ingredientForm.referenceCitation')} value={ing.ref_digest_source} isMono={false} />
            {showSources && <SourceRow source={getDataSource(ing.data_sources, "ref_digest_product")} />}
          </div>
          )}

          {/* Cost */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientDetail.cost')}</div>
            <NumField label={t('ingredientForm.costPerKg')} value={ing.cost_per_kg} />
            {showSources && <SourceRow source={getDataSource(ing.data_sources, "cost_per_kg")} />}
          </div>

          {/* Specs */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientForm.specs')}</div>
            <Field label={t('ingredientForm.subIngredients')} value={ing.sub_ingredients} isMono={false} />
            <Field label={t('ingredientForm.notesSpecRef')} value={ing.notes_spec_ref} isMono={false} />
          </div>

          {/* All Data Sources (audit view) */}
          {showSources && ing.data_sources && Object.keys(ing.data_sources).length > 0 && (
            <div style={sectionBox}>
              <div style={sectionTitle}>{t('ingredientDetail.allDataSources')}</div>
              {Object.entries(ing.data_sources).map(([key, val]) => (
                <div key={key} style={fieldRow}>
                  <div style={{ ...fieldLabel, fontWeight: key === "_default" ? 700 : 400, color: key === "_default" ? "#4a4a8a" : "#333" }}>
                    {key === "_default" ? t('ingredientDetail.default') : key}
                  </div>
                  <div style={fieldValue}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* System */}
          <div style={sectionBox}>
            <div style={sectionTitle}>{t('ingredientDetail.system')}</div>
            <Field label={t('ingredientDetail.createdAt')} value={ing.created_at} />
            <Field label={t('ingredientDetail.updatedAt')} value={ing.updated_at} />
            <Field label={t('ingredientDetail.updatedBy')} value={ing.updated_by_email} />
            <Field label={t('ingredientDetail.archivedAt')} value={ing.archived_at} />
            <Field label={t('ingredientDetail.archivedBy')} value={ing.archived_by_email} />
          </div>
        </div>
      )}

      {tab === "variants" && (
        <div style={{ color: "#666", fontSize: "12px", padding: "20px", textAlign: "center" }}>
          {t('ingredientDetail.variantsComingSoon')} ({variantCount} records)
        </div>
      )}

      {tab === "observations" && (
        <div style={{ color: "#666", fontSize: "12px", padding: "20px", textAlign: "center" }}>
          {t('ingredientDetail.observationsComingSoon')} ({obsCount} records)
        </div>
      )}

      {tab === "history" && (
        <div style={{ overflowX: "auto" }}>
          {logLoading ? (
            <div style={{ color: "#666", fontSize: "12px", padding: "20px", textAlign: "center" }}>{t('ingredientDetail.loadingHistory')}</div>
          ) : changeLog.length === 0 ? (
            <div style={{ color: "#666", fontSize: "12px", padding: "20px", textAlign: "center" }}>{t('ingredientDetail.noChangeHistory')}</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #d0d0d0", background: "#ffffff" }}>
              <thead>
                <tr>
                  {[t('ingredientDetail.dateColumn'), t('ingredientDetail.userColumn'), t('ingredientDetail.fieldColumn'), t('ingredientDetail.oldValue'), t('ingredientDetail.newValue'), t('ingredientDetail.reason')].map((h) => (
                    <th key={h} style={{ padding: "4px 8px", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", background: "#f0f0f0", border: "1px solid #d0d0d0", textAlign: "left", color: "#000" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {changeLog.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 8px", fontSize: "12px", border: "1px solid #d0d0d0", background: "#fff", fontFamily: mono, whiteSpace: "nowrap", color: "#000" }}>
                      {row.changed_at ? new Date(row.changed_at).toLocaleString() : "-"}
                    </td>
                    <td style={{ padding: "4px 8px", fontSize: "12px", border: "1px solid #d0d0d0", background: "#fff", color: "#000" }}>{row.changed_by_email || "-"}</td>
                    <td style={{ padding: "4px 8px", fontSize: "12px", border: "1px solid #d0d0d0", background: "#fff", fontFamily: mono, color: "#000" }}>{row.changed_column}</td>
                    <td style={{ padding: "4px 8px", fontSize: "12px", border: "1px solid #d0d0d0", background: "#fff", color: row.old_value ? "#000" : "#999", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>{row.old_value ?? "-"}</td>
                    <td style={{ padding: "4px 8px", fontSize: "12px", border: "1px solid #d0d0d0", background: "#fff", color: row.new_value ? "#000" : "#999", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>{row.new_value ?? "-"}</td>
                    <td style={{ padding: "4px 8px", fontSize: "12px", border: "1px solid #d0d0d0", background: "#fff", color: row.change_reason ? "#000" : "#999" }}>{row.change_reason ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "attachments" && (
        <div style={{ color: "#666", fontSize: "12px", padding: "20px", textAlign: "center" }}>
          {t('ingredientDetail.attachmentsComingSoon')} ({attachCount} records)
        </div>
      )}
    </div>
  );
}
